/**
 * How depth is drawn: real meters to a share of the aquarium's height.
 *
 * **Two scales, the break at 1,000 m** (the user's call, M17): drawn true, the
 * Maui Nui box would be mostly cutaway rock and deep water, with the shelf and
 * its reefs a sliver at the top. So the top 1,000 m, the sunlit and twilight
 * zones, take three quarters of the box, and everything below shares the last
 * quarter. That is where the life is: in profiles from the surface to 5,440 m
 * over the Madeira Abyssal Plain, more than 80% of the plankton and more than
 * 95% of the micronekton lay in the top 1,000 m (H. S. J. Roe's profiles, as
 * reported in the paper's abstract); the fish of the twilight zone, 200 to
 * 1,000 m, are put at 11,000 to 15,000 million tonnes between 40°N and 40°S
 * (Irigoien et al. 2014, *Nature Communications* 5:3271, as its authors
 * summarized it); and at Maui Nui the reef, the humpbacks, the monk seals and
 * the turtles live over the shelf, shallower than 100 fathoms (182.88 m).
 *
 * Land rises at the deep scale, so the islands read as islands and not spires.
 * Every scale is printed on the box (`aquariumView.ts`). Where a site's water
 * is shallower than the break, the whole column is drawn at one scale.
 *
 * Pure arithmetic, with no Three.js, so `tests/depthAxis.test.ts` can hold it.
 */

/** The depth the scale changes at, m: the floor of the twilight zone. DECISION (the user's, M17). */
export const BREAK_M = 1000;
/** The share of the box's height above the break. DECISION (the user's "most of the cube", M17). */
export const UPPER_SHARE = 0.75;

export interface DepthAxis {
  /** The deepest water drawn, m: the box's floor. */
  deepestM: number;
  /** Where the scale changes, m; the deepest water when the site is shallower than `BREAK_M`. */
  breakM: number;
  /** The share of the height above the break. */
  upperShare: number;
}

export function depthAxis(deepestM: number): DepthAxis {
  if (deepestM <= BREAK_M) return { deepestM, breakM: deepestM, upperShare: 1 };
  return { deepestM, breakM: BREAK_M, upperShare: UPPER_SHARE };
}

/** How far down the box a depth is drawn: 0 at the surface, 1 at the floor. */
export function depthShare(axis: DepthAxis, depthM: number): number {
  const d = Math.min(Math.max(depthM, 0), axis.deepestM);
  if (d <= axis.breakM) return (axis.upperShare * d) / axis.breakM;
  return axis.upperShare + ((1 - axis.upperShare) * (d - axis.breakM)) / (axis.deepestM - axis.breakM);
}

/** The depth drawn `share` of the way down the box, m: `depthShare` turned around. */
export function depthAtShare(axis: DepthAxis, share: number): number {
  const s = Math.min(Math.max(share, 0), 1);
  if (s <= axis.upperShare) return (s * axis.breakM) / axis.upperShare;
  return axis.breakM + ((s - axis.upperShare) * (axis.deepestM - axis.breakM)) / (1 - axis.upperShare);
}

/** How far above the surface land is drawn, in shares of the box's height: the deep scale. */
export function landShare(axis: DepthAxis, heightM: number): number {
  const below = axis.deepestM - axis.breakM;
  return below > 0 ? ((1 - axis.upperShare) * heightM) / below : (axis.upperShare * heightM) / axis.breakM;
}

/**
 * How many times depth is stretched against the horizontal, above and below
 * the break, for a box `heightUnits` tall over `sideM` drawn `sideUnits` wide.
 */
export function stretches(axis: DepthAxis, sideM: number, sideUnits: number, heightUnits: number): { upper: number; lower: number } {
  const across = sideUnits / sideM;
  const below = axis.deepestM - axis.breakM;
  const upper = (axis.upperShare * heightUnits) / axis.breakM / across;
  return { upper, lower: below > 0 ? ((1 - axis.upperShare) * heightUnits) / below / across : upper };
}

/** A label on the depth ruler, where it falls on screen. */
export interface RulerLabel {
  depthM: number;
  /** Its center's height on screen, px from the top. */
  screenY: number;
  /** Its height on screen, px. */
  heightPx: number;
  /** The surface, the break and the floor: always shown. */
  keep: boolean;
}

/**
 * Which ruler labels show (review M18): the kept ones first, then the rest from
 * the surface down, each only where it clears every label already shown by a
 * tenth more than its own height. On a small screen the compressed deep scale
 * puts its ticks a few pixels apart, and drawn all at once they were a smear.
 */
export function placeRulerLabels(labels: readonly RulerLabel[]): boolean[] {
  const order = labels.map((_, i) => i).sort((a, b) => {
    const la = labels[a] as RulerLabel;
    const lb = labels[b] as RulerLabel;
    return Number(lb.keep) - Number(la.keep) || la.depthM - lb.depthM;
  });
  const shown = labels.map(() => false);
  const placed: number[] = [];
  for (const i of order) {
    const l = labels[i] as RulerLabel;
    if (l.keep || placed.every((y) => Math.abs(y - l.screenY) >= 1.1 * l.heightPx)) {
      shown[i] = true;
      placed.push(l.screenY);
    }
  }
  return shown;
}
